import {
  Alert,
  Avatar,
  Button,
  Card,
  Drawer,
  Group,
  Image,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Text,
  Title,
  useMantineTheme
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconAlertCircle, IconClipboardList } from '@tabler/icons'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'

import { useApplications } from '@/lib/hooks'
import { api, notificationHandler } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'
import { useSiteContext } from '@/providers/SiteContextProvider'
import { EnrollmentApplication as EnrollmentApplicationType } from '@/types'

import ApplicationCard from './ApplicationCard'

interface Props {
  opened: boolean
  onClose: () => void
}

export default function ApplicationsDrawer({ opened, onClose }: Props) {
  // Hooks
  const theme = useMantineTheme()
  const router = useRouter()
  const isXs = useMediaQuery(`(max-width: ${theme.breakpoints.xs}px)`)
  const { isAuthenticated } = useAuth() || {}
  const { enrollmentWorkspace } = useSiteContext()
  const { data, error, isLoading, mutate } = useApplications(enrollmentWorkspace)

  // States
  const [remove, setRemove] = useState<EnrollmentApplicationType | null>(null)

  // Constants
  const { site: siteSlug } = router.query || {}
  const applications: EnrollmentApplicationType[] = data?.results || []

  // Actions
  const handleRemove = () => {
    if (!remove) return null
    api
      .delete(`/${siteSlug}/enrollment-store/enrollment-applications/${remove?.uid}/`)
      .then(() => {
        setRemove(null)
        mutate()
        notificationHandler({
          variant: 'success',
          message: 'Matrícula removida com sucesso'
        })
      })
      .catch(() => {
        notificationHandler({
          variant: 'error',
          message: 'Não foi possível remover a matrícula. Tente novamente mais tarde.'
        })
      })
  }

  // Render
  if (!isAuthenticated) return null

  return (
    <>
      <Drawer
        opened={opened}
        onClose={onClose}
        size={isXs ? 'full' : 480}
        position="right"
        padding="lg"
        title={<Title order={3}>Matrículas em andamento</Title>}>
        <Stack>
          <Text size="sm" color="dimmed" mt={4}>
            Continue do ponto em que você parou.
          </Text>
          <ScrollArea style={{ height: 'calc(100vh - 120px)' }} offsetScrollbars>
            <Stack spacing="md">
              {isLoading && !data && (
                <Group position="center" py="xl">
                  <Loader />
                </Group>
              )}

              {!!error && !data && (
                <Alert icon={<IconAlertCircle size={16} />} color="red">
                  Não foi possível carregar suas matrículas. Tente novamente mais tarde.
                </Alert>
              )}

              {!!data && !applications.length && (
                <Card withBorder>
                  <Stack align="center" py="xl">
                    <IconClipboardList size={40} color={theme.colors.gray[5]} />
                    <Text weight={600} align="center">
                      Nenhuma matrícula em andamento
                    </Text>
                    <Text size="sm" color="dimmed" align="center">
                      As matrículas que você iniciar aparecerão aqui.
                    </Text>

                    <Button
                      variant="outline"
                      component={Link}
                      href="/courses"
                      hidden={!enrollmentWorkspace}>
                      Ver cursos
                    </Button>
                  </Stack>
                </Card>
              )}

              {applications.map(application => (
                <ApplicationCard
                  key={application.uid}
                  application={application}
                  onContinue={onClose}
                  onRemove={application => setRemove(application)}
                />
              ))}
            </Stack>
          </ScrollArea>
        </Stack>
      </Drawer>
      <Modal opened={!!remove} onClose={() => setRemove(null)} title="Remover matrícula" centered>
        <Stack>
          <Card withBorder>
            <Group>
              <Avatar>
                <Image src={remove?.offering?.course?.image} alt="Avatar" width={40} height={40} />
              </Avatar>
              <strong>{remove?.offering?.course?.name}</strong>
            </Group>
          </Card>
          <Text>
            Tem certeza de que deseja remover a inscrição do aluno{' '}
            <strong>
              {[remove?.studentSiteProfile?.firstName, remove?.studentSiteProfile?.lastName]
                .filter(Boolean)
                .join(' ')}
            </strong>
            ?
          </Text>
          <Group position="right">
            <Button color="red" onClick={handleRemove}>
              Remover
            </Button>
            <Button variant="default" onClick={() => setRemove(null)}>
              Cancelar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
